import React, { useState, useEffect, useMemo } from 'react';
import { Stethoscope, Plus, Edit, Trash2, Filter, Users2, Layers, X } from 'lucide-react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import api from '../../services/api';
import styles from './SettingsPage.module.css';

const getInitialProjectId = () => {
  if (typeof window === 'undefined') return '';
  const stored = localStorage.getItem('selected_project_id');
  if (!stored) return '';
  const parsed = Number(stored);
  return Number.isNaN(parsed) ? '' : parsed;
};

const DiagnosticCodeList = () => {
  const [codes, setCodes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedProject, setSelectedProject] = useState(getInitialProjectId);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    project: '',
    customers: [],
  });

  useEffect(() => {
    fetchProjects();
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (!selectedProject) {
      setCodes([]);
      return;
    }
    fetchCodes();
  }, [selectedProject, selectedCustomer]);

  const projectOptions = useMemo(
    () => projects.map(project => ({ value: project.id, label: project.name })),
    [projects]
  );

  const customerOptions = useMemo(
    () => customers.map(customer => ({ value: customer.id, label: customer.name })),
    [customers]
  );

  const fetchProjects = async () => {
    try {
      const { data } = await api.get('/settings/projects/');
      setProjects(data);
    } catch (error) {
      toast.error('Не удалось загрузить проекты');
    }
  };

  const fetchCustomers = async () => {
    try {
      const { data } = await api.get('/settings/customers/');
      setCustomers(data);
    } catch (error) {
      toast.error('Не удалось загрузить заказчиков');
    }
  };

  const fetchCodes = async () => {
    if (!selectedProject) return;
    try {
      setLoading(true);
      const params = { project_id: selectedProject };
      if (selectedCustomer) {
        params.customer_id = selectedCustomer;
      }
      const { data } = await api.get('/repairs/diagnostic-codes/', { params });
      setCodes(data);
    } catch (error) {
      toast.error('Ошибка загрузки кодов диагностики');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    if (!selectedProject) {
      toast.warn('Сначала выберите проект');
      return;
    }
    setFormData({
      code: '',
      name: '',
      description: '',
      project: selectedProject,
      customers: [],
    });
    setEditingId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      project: '',
      customers: [],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.project) {
      toast.error('Не выбран проект');
      return;
    }
    if (!formData.code.trim() || !formData.name.trim()) {
      toast.error('Заполните обязательные поля');
      return;
    }

    const payload = {
      code: formData.code.trim(),
      name: formData.name.trim(),
      description: formData.description,
      project_id: formData.project,
      customer_ids: formData.customers.map(option => option.value),
    };

    try {
      if (editingId) {
        await api.put(`/repairs/diagnostic-codes/${editingId}/`, payload);
        toast.success('Код диагностики обновлён');
      } else {
        await api.post('/repairs/diagnostic-codes/', payload);
        toast.success('Код диагностики создан');
      }
      closeModal();
      fetchCodes();
    } catch (error) {
      toast.error('Ошибка сохранения кода диагностики');
    }
  };

  const handleEdit = (item) => {
    setFormData({
      code: item.code,
      name: item.name,
      description: item.description || '',
      project: item.project?.id || selectedProject || '',
      customers: customerOptions.filter(option =>
        item.customers.some(customer => customer.id === option.value)
      ),
    });
    setEditingId(item.id);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить код диагностики?')) return;
    try {
      await api.delete(`/repairs/diagnostic-codes/${id}/`);
      toast.success('Код диагностики удалён');
      fetchCodes();
    } catch (error) {
      toast.error('Ошибка удаления кода диагностики');
    }
  };

  const renderCustomers = (item) => {
    if (!item.customers?.length) {
      return <span className={styles.mutedText}>—</span>;
    }
    return item.customers.map(customer => (
      <span key={customer.id} className={styles.pill}>
        {customer.name}
      </span>
    ));
  };

  const selectedProjectOption = projectOptions.find(option => option.value === selectedProject) || null;
  const selectedCustomerOption = customerOptions.find(option => option.value === selectedCustomer) || null;

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h3>
          <Stethoscope size={24} style={{ marginRight: '0.5rem', color: '#0f766e' }} />
          Коды диагностики
        </h3>
        <button className={styles.addButton} onClick={openAddModal} disabled={!selectedProject}>
          <Plus size={18} />
          Добавить код
        </button>
      </div>

      <div className={styles.filtersRow}>
        <div className={styles.filterBlock}>
          <label>
            <Filter size={14} />
            Проект
          </label>
          <Select
            className={styles.select}
            classNamePrefix="react-select"
            placeholder="Выберите проект"
            value={selectedProjectOption}
            onChange={(option) => setSelectedProject(option ? option.value : '')}
            options={projectOptions}
            isClearable
          />
        </div>
        <div className={styles.filterBlock}>
          <label>
            <Users2 size={14} />
            Заказчик
          </label>
          <Select
            className={styles.select}
            classNamePrefix="react-select"
            placeholder="Все заказчики"
            value={selectedCustomerOption}
            onChange={(option) => setSelectedCustomer(option ? option.value : '')}
            options={customerOptions}
            isClearable
          />
        </div>
      </div>

      {!selectedProject && (
        <div className={styles.infoBanner}>
          Выберите проект, чтобы увидеть связанные коды диагностики.
        </div>
      )}

      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingContent}>
            <div className={styles.spinner}></div>
            <p className={styles.loadingText}>Загрузка кодов диагностики...</p>
          </div>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          {codes.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <Stethoscope size={48} />
              </div>
              <h3>Нет кодов диагностики</h3>
              <p>Добавьте первый код, чтобы использовать его в карточках оборудования</p>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Код</th>
                  <th>Название</th>
                  <th>Проект</th>
                  <th>Заказчики</th>
                  <th>Описание</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {codes.map(item => (
                  <tr key={item.id} className={styles.clickableRow} onClick={() => handleEdit(item)}>
                    <td>
                      <div className={styles.inlineCode}>
                        <Layers size={14} />
                        {item.code}
                      </div>
                    </td>
                    <td><strong>{item.name}</strong></td>
                    <td>{item.project?.name || '—'}</td>
                    <td className={styles.pillColumn}>{renderCustomers(item)}</td>
                    <td>{item.description || <span className={styles.mutedText}>—</span>}</td>
                    <td>
                      <button className={styles.actionButton} onClick={(event) => { event.stopPropagation(); handleDelete(item.id); }} title="Удалить">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                <Stethoscope size={20} />
                {editingId ? 'Редактировать код' : 'Добавить код диагностики'}
              </h3>
              <button className={styles.modalCloseButton} onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.formGroup}>
                  <label>Проект</label>
                  <Select
                    className={styles.select}
                    classNamePrefix="react-select"
                    placeholder="Выберите проект"
                    value={projectOptions.find(option => option.value === formData.project) || null}
                    onChange={(option) => setFormData({ ...formData, project: option ? option.value : '' })}
                    options={projectOptions}
                    isClearable
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Код</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="Например, ERR-0101"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Название</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Короткое описание проблемы"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Описание</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Расширенное описание"
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Заказчики</label>
                  <Select
                    className={styles.select}
                    classNamePrefix="react-select"
                    placeholder="Выберите заказчиков"
                    value={formData.customers}
                    onChange={(value) => setFormData({ ...formData, customers: value || [] })}
                    options={customerOptions}
                    isMulti
                  />
                </div>
                <button type="submit" className={styles.submitButton}>
                  {editingId ? 'Сохранить изменения' : 'Создать код'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiagnosticCodeList;
